import React, { useState, useEffect, useRef } from 'react'
import { Box, SkeletonCircle, Flex, Text } from '@chakra-ui/react'

interface FastAvatarProps {
  src?: string
  name?: string
  size?: string
  boxSize?: string
  fallbackSrc?: string
  [key: string]: any
}

const FastAvatar: React.FC<FastAvatarProps> = ({ 
  src, 
  name, 
  size = 'md',
  boxSize,
  fallbackSrc,
  ...props 
}) => {
  const [imageLoaded, setImageLoaded] = useState(false)
  const [imageError, setImageError] = useState(false)
  const [showSkeleton, setShowSkeleton] = useState(true)
  const [isInView, setIsInView] = useState(false)
  const imgRef = useRef<HTMLImageElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  // Size mapping
  const sizeMap = {
    'xs': '24px',
    'sm': '32px', 
    'md': '40px',
    'lg': '56px',
    'xl': '72px',
    '2xl': '96px'
  }

  const avatarSize = boxSize || sizeMap[size as keyof typeof sizeMap] || size

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true)
            observer.disconnect() 
          }
        })
      },
      { threshold: 0.1, rootMargin: '50px' } 
    )

    if (containerRef.current) {
      observer.observe(containerRef.current)
    }

    return () => {
      observer.disconnect()
    }
  }, [])

  useEffect(() => {
    if (!src || !isInView) {
      setShowSkeleton(false)
      return
    }

    const img = new Image()
    
    img.onload = () => {
      setImageLoaded(true)
      setShowSkeleton(false)
    }
    
    img.onerror = () => {
      setImageError(true)
      setShowSkeleton(false)
    }

    img.src = src
    
    const skeletonTimeout = setTimeout(() => {
      setShowSkeleton(false)
    }, 100)

    return () => {
      clearTimeout(skeletonTimeout)
      img.onload = null
      img.onerror = null
    }
  }, [src, isInView])

  if (!src || imageError) {
    const initials = name
      ? name.split(' ').map((n) => n[0]).filter(Boolean).slice(0, 2).join('').toUpperCase()
      : ''
    const fontSize = parseInt(avatarSize) <= 32 ? '11px' : parseInt(avatarSize) <= 40 ? '13px' : '15px'
    return (
      <Flex
        width={avatarSize}
        height={avatarSize}
        minW={avatarSize}
        borderRadius="full"
        bg="solid.50"
        align="center"
        justify="center"
        {...props}
      >
        {initials && (
          <Text fontSize={fontSize} fontWeight="600" color="solid.400" lineHeight="1" userSelect="none">
            {initials}
          </Text>
        )}
      </Flex>
    )
  }

  return (
    <Box
      ref={containerRef}
      position="relative"
      width={avatarSize}
      height={avatarSize}
      borderRadius="full"
      overflow="hidden"
      {...props}
    >
      {showSkeleton && (
        <SkeletonCircle 
          size={avatarSize} 
          position="absolute"
          top="0"
          left="0"
          zIndex="2"
        />
      )}
      
      {isInView && (
        <img
          ref={imgRef}
          src={src}
          alt={name || 'Profile picture'}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            borderRadius: '50%',
            opacity: imageLoaded ? 1 : 0.8, 
            transition: imageLoaded ? 'opacity 0.15s ease-out' : 'none',
            display: 'block'
          }}
          loading="lazy" 
          decoding="async" 
        />
      )}
    </Box>
  )
}

export default FastAvatar